import { useContext, useEffect, useState } from "react";
import Talk from "talkjs";
import { mapToTalkUser } from "../utils/TalkjsUtils";
import { useGetUser } from "./useGetUser";
import * as crypto from "crypto";
import { UserDetailsContext } from "../contexts";

type ReturnType = {
  autoCompleteOptions: any;
  isChatLoading: boolean;
  session: Talk.Session | undefined;
  setInbox: React.Dispatch<React.SetStateAction<Talk.Inbox | undefined>>;
  setParticipants: React.Dispatch<any>;
  createConversation: (
    conversationId?: string,
    conversationSubject?: string
  ) => void;
  autoCreateChat: boolean;
  setAutoCreateChat: React.Dispatch<React.SetStateAction<boolean>>;
};
export const useTalk = (userId: string): ReturnType => {
  const [talkLoaded, markTalkLoaded] = useState(false);
  const { allUsers } = useContext(UserDetailsContext);
  const [participants, setParticipants] = useState<Array<Talk.User>>([]);
  const [session, setSession] = useState<Talk.Session>();
  const [inbox, setInbox] = useState<Talk.Inbox>();
  const { user, isUserLoading } = useGetUser(userId);
  const [isLoading, setIsLoading] = useState(true);
  const [autoCreateChat, setAutoCreateChat] = useState(false);

  useEffect(() => {
    Talk.ready.then(() => markTalkLoaded(true));
  }, [userId]);

  useEffect(() => {
    try {
      if (talkLoaded && user) {
        const talkSession = new Talk.Session({
          appId: process.env.REACT_APP_TALKJS_ID!,
          signature: user.getChatSignature().toUpperCase(),
          me: mapToTalkUser(user),
        });
        setSession(talkSession);
        setIsLoading(false);
        return () => talkSession.destroy();
      }
    } catch (error) {
      console.log(error);
    }
  }, [user, talkLoaded]);

  const createConversation = (
    conversationId?: string,
    conversationSubject?: string
  ) => {
    try {
      if (talkLoaded && user && session && inbox) {
        setIsLoading(true);
        const talkCurrentUser = mapToTalkUser(user);
        participants.push(talkCurrentUser);
        const predictableConversationId: string = [
          ...participants,
          talkCurrentUser,
        ]
          .map((u) => u.id)
          .join();
        if (!conversationId) {
          conversationId = crypto
            .createHmac("SHA256", "secret")
            .update(predictableConversationId)
            .digest("base64");
        }
        const conversation = session.getOrCreateConversation(conversationId);
        conversation.subject = conversationSubject;
        participants.forEach((participant) => {
          conversation.setParticipant(participant);
        });
        inbox.select(conversation);
        setIsLoading(false);
      }
    } catch (error) {
      console.log(error);
    }
  };

  return {
    autoCompleteOptions: allUsers.filter(
      (it) => it.getUsername() !== user?.getUsername()
    ),
    isChatLoading: isLoading || isUserLoading,
    session,
    setInbox,
    setParticipants,
    createConversation,
    autoCreateChat,
    setAutoCreateChat,
  };
};
